import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import {
  Grid,
  LinearProgress,
  Typography,
  Button,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { head, has, get, isNull } from "lodash";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Pie, Doughnut } from 'react-chartjs-2';

// import { SocketContext } from "../../context/Socket/SocketContext";

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  textRight: {
    textAlign: "right",
  },
  tabPanelsContainer: {
    padding: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  tableTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 500,
  },
  chartContainer: {
    height: 300,
    marginBottom: theme.spacing(4),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  fullWidthGrid: {
    width: '100%',
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const CampaignReport = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [shippingPage, setShippingPage] = useState(1);
  const [totalShippingPages, setTotalShippingPages] = useState(1);
  const [uniqueNumbers, setUniqueNumbers] = useState(0);
  const [failedMessages, setFailedMessages] = useState(0);
  const [totalMessagesSent, setTotalMessagesSent] = useState(0);
  const [whatsappChartData, setWhatsappChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          '#33b5e5', // Vibrant Blue
          '#aa66cc', // Vibrant Purple
          '#FF8800', // Vibrant Orange
          '#ff4444', // Vibrant Red
          '#00C851', // Vibrant Green
        ],
        borderColor: [
          '#0099CC',
          '#9933CC',
          '#CC0000',
          '#CC0000',
          '#007E33',
        ],
        borderWidth: 1,
      },
    ],
  });
  const mounted = useRef(true);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  // Dados para o gráfico de pizza
  const [chartData, setChartData] = useState({
    labels: [i18n.t("campaignReport.chart.delivered"), i18n.t("campaignReport.chart.pending"), i18n.t("campaignReport.chart.failed")],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          '#00C851', // Vibrant Green
          '#FFC107', // Mustard/Yellow (Pendientes)
          '#ff4444', // Vibrant Red
        ],
        borderColor: [
          '#007E33',
          '#FF8800',
          '#CC0000',
        ],
        borderWidth: 1,
      },
    ],
  });

  // Opções para o gráfico de pizza
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme.palette.text.primary,
        },
      },
    },
  };

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan?.useCampaigns) {
        toast.error(i18n.t("toastError.permissionDenied"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      if (has(campaign, "contactList") && has(campaign.contactList, "contacts")) {
        const contactList = get(campaign, "contactList");
        const valids = contactList.contacts.filter((c) => c.isWhatsappValid);
        setValidContacts(valids.length);
      } else if (has(campaign, "tagListId")) {
        // Se estamos usando tags em vez de listas de contato
        setValidContacts(delivered); // Usamos as entregas como contatos válidos
      }

      if (has(campaign, "shipping") && Array.isArray(campaign.shipping)) {
        const contacts = get(campaign, "shipping");
        const delivered = contacts.filter((c) => !isNull(c.deliveredAt));
        const confirmationRequested = contacts.filter(
          (c) => !isNull(c.confirmationRequestedAt)
        );
        const confirmed = contacts.filter(
          (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
        );
        setDelivered(delivered.length);
        setConfirmationRequested(confirmationRequested.length);
        setConfirmed(confirmed.length);
      }
    }
  }, [campaign]);

  useEffect(() => {
    // Evitar divisão por zero
    if (validContacts > 0) {
      setPercent((delivered / validContacts) * 100);
    } else {
      setPercent(0);
    }
  }, [delivered, validContacts]);

  const fetchStats = async () => {
    try {
      const { data: stats } = await api.get(`/campaigns/${campaignId}/stats`);
      setDelivered(stats.deliveredMessages);
      setValidContacts(stats.totalMessages);
      setUniqueNumbers(stats.uniqueNumbers);
      setFailedMessages(stats.failedMessages);
      setTotalMessagesSent(stats.totalMessages);

      let calculatedPending = stats.pendingMessages;
      const diff = stats.totalMessages - stats.deliveredMessages - stats.failedMessages;

      if (diff > 0 && (calculatedPending === 0 || calculatedPending === undefined)) {
        calculatedPending = diff;
      }

      setChartData({
        labels: [i18n.t("campaignReport.chart.delivered"), i18n.t("campaignReport.chart.pending"), i18n.t("campaignReport.chart.failed")],
        datasets: [
          {
            data: [stats.deliveredMessages, calculatedPending, stats.failedMessages],
            backgroundColor: [
              '#00C851',
              '#FFC107',
              '#ff4444',
            ],
            borderColor: [
              '#007E33',
              '#FF8800',
              '#CC0000',
            ],
            borderWidth: 1,
          },
        ],
      });

      if (stats.whatsappDistribution && stats.whatsappDistribution.length > 0) {
        const wLabels = stats.whatsappDistribution.map(w => w.name);
        const wData = stats.whatsappDistribution.map(w => w.count);
        setWhatsappChartData(prev => ({
          ...prev,
          labels: wLabels,
          datasets: [{
            ...prev.datasets[0],
            data: wData
          }]
        }));
      }
    } catch (statsError) {
      console.error("Erro ao buscar estatísticas:", statsError);
      setDelivered(0);
      setValidContacts(0);
      setUniqueNumbers(0);
      setFailedMessages(0);
      setTotalMessagesSent(0);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onCampaignEvent = (data) => {

      if (data.record.id === +campaignId) {
        setCampaign(data.record);

        if (data.action === "update") {
           // Silently update the current page to avoid full screen reloads
           fetchStats();
           loadMoreShipping(page + 1, rowsPerPage, true);
        }

        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findCampaign();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-campaign`, onCampaignEvent);

    return () => {
      socket.off(`company-${companyId}-campaign`, onCampaignEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, page, rowsPerPage]);

  const findCampaign = async () => {
    try {
      setLoading(true);

      // Buscar dados básicos da campanha (sem shipping para evitar estouro de memória)
      const { data: campaign } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(campaign);

      // Buscar estatísticas separadamente usando o novo endpoint
      await fetchStats();

      // Buscar dados de shipping com paginação usando o novo endpoint
      try {
        const { data: shippingResponse } = await api.get(`/campaigns/${campaignId}/shipping?page=1&pageSize=${rowsPerPage}`);

        if (shippingResponse && shippingResponse.shipping) {
          const shippingData = shippingResponse.shipping;

          // Processar dados para a tabela
          const formattedRows = shippingData.map(item => ({
            id: item.id,
            jobId: item.jobId,
            number: item.number,
            message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? '...' : '') : '',
            fullMessage: item.message || '',
            deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
            createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
            status: (item.message && item.message.startsWith('[ERROR')) ? 'failed' : (item.deliveredAt ? 'delivered' : 'pending')
          }));

          setMessageRows(formattedRows);

          // Armazenar informações de paginação
          setTotalShippingPages(shippingResponse.totalPages || 1);
          setShippingPage(shippingResponse.currentPage || 1);
        }
      } catch (shippingError) {
        console.error("Erro ao buscar dados de envio:", shippingError);
        // Manter array vazio em caso de erro
        setMessageRows([]);
      }

    } catch (error) {
      console.error("Erro ao buscar campanha:", error);
      toast.error(i18n.t("campaignReport.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaignReport.inactive");
      case "PROGRAMADA":
        return i18n.t("campaignReport.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaignReport.process");
      case "CANCELADA":
        return i18n.t("campaignReport.cancelled");
      case "FINALIZADA":
        return i18n.t("campaignReport.finished");
      default:
        return val;
    }
  };

  const getLocale = () => {
    switch (i18n.language) {
      case 'es': return 'es-ES';
      case 'en': return 'en-US';
      default: return 'pt-BR';
    }
  }

  // Manipuladores para a paginação da tabela
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Carregar dados com novo tamaño de página
    loadMoreShipping(1, parseInt(event.target.value, 10));
  };

  // Função para carregar mais dados de shipping
  const loadMoreShipping = async (newPage = 1, pageSize = 50, silent = false) => {
    try {
      if (!silent) setLoading(true);

      const url = `/campaigns/${campaignId}/shipping?page=${newPage}&pageSize=${pageSize}`;
      const { data: shippingResponse } = await api.get(url);

      if (shippingResponse && shippingResponse.shipping) {
        const shippingData = shippingResponse.shipping;

        // Processar dados para a tabela
        const formattedRows = shippingData.map(item => ({
          id: item.id,
          jobId: item.jobId,
          number: item.number,
          message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? '...' : '') : '',
          fullMessage: item.message || '',
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
          status: item.deliveredAt ? 'delivered' : 'pending'
        }));

        setMessageRows(formattedRows);
        setTotalShippingPages(shippingResponse.totalPages || 1);
        setShippingPage(shippingResponse.currentPage || 1);

      } else {
        setMessageRows([]);
      }
    } catch (error) {
      console.error("Erro ao carregar mais dados de envio:", error);
      if (!silent) toast.error(i18n.t("campaignReport.fetchError"));
      setMessageRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Função para formatar o número de telefone
  const formatPhoneNumber = (number) => {
    if (!number) return '';

    // Verifica se é um ID de grupo (normalmente começa com números grandes)
    if (number.length > 15) {
      return number;
    }

    // Formata número normal
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }

    return number;
  };

  // Função para obter o status do envio
  const getMessageStatusChip = (status) => {
    switch (status) {
      case 'delivered':
        return (
          <Chip
            label={i18n.t("campaignReport.chip.delivered")}
            size="small"
            icon={<DoneAllIcon fontSize="small" />}
            className={`${classes.statusChip} ${classes.deliveredChip}`}
          />
        );
      case 'pending':
        return (
          <Chip
            label={i18n.t("campaignReport.chip.pending")}
            size="small"
            icon={<ScheduleIcon fontSize="small" />}
            className={`${classes.statusChip} ${classes.pendingChip}`}
          />
        );
      case 'failed':
        return (
          <Chip
            label={i18n.t("campaignReport.chip.failed")}
            size="small"
            icon={<ErrorIcon fontSize="small" />}
            className={`${classes.statusChip} ${classes.failedChip}`}
          />
        );
      default:
        return (
          <Chip
            label={i18n.t("campaignReport.chip.unknown")}
            size="small"
            variant="outlined"
          />
        );
    }
  };

  // Função para exportar dados para Excel
  const exportToExcel = async () => {
    try {
      toast.info(i18n.t("campaignReport.excel.generating"));

      // Buscar todos os dados de shipping em lotes (máximo 1000 por página)
      let allShippingData = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000; // Limite máximo do backend

      while (hasMoreData) {
        const { data: shippingResponse } = await api.get(`/campaigns/${campaignId}/shipping?page=${currentPage}&pageSize=${pageSize}`);

        if (!shippingResponse || !shippingResponse.shipping) {
          break;
        }

        allShippingData = [...allShippingData, ...shippingResponse.shipping];

        // Verificar se há mais páginas
        hasMoreData = currentPage < shippingResponse.totalPages;
        currentPage++;

        // Atualizar progresso
        toast.info(`${i18n.t("campaignReport.excel.loading")} ${allShippingData.length}`);
      }

      if (allShippingData.length === 0) {
        toast.error(i18n.t("campaignReport.excel.notFound"));
        return;
      }

      const shippingData = allShippingData;

      // Preparar dados para Excel
      const excelData = [
        // Cabeçalho
        [i18n.t("campaignReport.title"), "", "", "", "", ""],
        [`${i18n.t("campaignReport.campaign")}: ${campaign.name}`, "", "", "", "", ""],
        [`Data de Geração: ${new Date().toLocaleString(getLocale())}`, "", "", "", "", ""],
        ["", "", "", "", "", ""],
        // Cabeçalhos das colunas
        [
          i18n.t("campaignReport.table.id"),
          i18n.t("campaignReport.table.recipient"),
          i18n.t("campaignReport.table.message"),
          i18n.t("campaignReport.table.sentAt"),
          i18n.t("campaignReport.table.deliveredAt"),
          i18n.t("campaignReport.table.status")
        ],
      ];

      // Adicionar dados das mensagens
      shippingData.forEach(item => {
        excelData.push([
          item.jobId || '',
          item.number || '',
          item.message || '',
          item.createdAt ? new Date(item.createdAt).toLocaleString(getLocale()) : '',
          item.deliveredAt ? new Date(item.deliveredAt).toLocaleString(getLocale()) : '',
          (item.message && item.message.startsWith('[ERROR')) ? i18n.t("campaignReport.chip.failed") : (item.deliveredAt ? i18n.t("campaignReport.chip.delivered") : i18n.t("campaignReport.chip.pending"))
        ]);
      });

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Configurar larguras das colunas
      ws['!cols'] = [
        { wch: 15 }, // ID
        { wch: 20 }, // Destinatário
        { wch: 50 }, // Mensagem
        { wch: 20 }, // Enviado em
        { wch: 20 }, // Entregue em
        { wch: 15 }  // Status
      ];

      // Mesclar células do cabeçalho
      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 5, r: 0 } }, // Título
        { s: { c: 0, r: 1 }, e: { c: 5, r: 1 } }, // Nome da campanha
        { s: { c: 0, r: 2 }, e: { c: 5, r: 2 } }  // Data de geração
      ];

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, i18n.t("campaignReport.title"));

      // Gerar nome del archivo
      const fileName = `relatorio_campanha_${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Fazer download
      XLSX.writeFile(wb, fileName);

      toast.success(`${i18n.t("campaignReport.excel.success")} ${shippingData.length}`);

    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error(i18n.t("campaignReport.excel.error"));
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/campaigns')}
              startIcon={<ArrowBackIcon />}
            >
              {i18n.t("campaignReport.backButton")}
            </Button>
            <Title>{i18n.t("campaignReport.title")} {campaign.name || i18n.t("campaignReport.campaign")}</Title>
          </Grid>
        </Grid>
      </MainHeader>

      {/* Card com resumo e status */}
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign.name || i18n.t("campaignReport.campaign")}
          </Typography>
          <Chip
            label={formatStatus(campaign.status)}
            color={campaign.status === "FINALIZADA" ? "primary" : "default"}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>

        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {i18n.t("campaignReport.deliveryStatus")}
                </Typography>
                <Box className={classes.pieChartContainer} display="flex" justifyContent="center" alignItems="center">
                  {chartData.datasets[0].data.some(v => v > 0) ? (
                    <Doughnut data={chartData} options={chartOptions} />
                  ) : (
                    <Doughnut
                      data={{
                        labels: [i18n.t("campaignReport.table.noData")],
                        datasets: [{
                          data: [1],
                          backgroundColor: ['#eeeeee'],
                          borderColor: ['#e0e0e0'],
                          borderWidth: 1,
                        }]
                      }}
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          tooltip: { enabled: false },
                          legend: { display: false },
                          datalabels: { display: false }
                        }
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Card de Distribución por WhatsApp */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {i18n.t("campaignReport.whatsappDistribution")}
                </Typography>
                <Box className={classes.pieChartContainer} display="flex" justifyContent="center" alignItems="center">
                  {whatsappChartData.datasets[0].data.length > 0 ? (
                    <Doughnut data={whatsappChartData} options={chartOptions} />
                  ) : (
                    <Doughnut
                      data={{
                        labels: [i18n.t("campaignReport.table.noData")],
                        datasets: [{
                          data: [1],
                          backgroundColor: ['#eeeeee'],
                          borderColor: ['#e0e0e0'],
                          borderWidth: 1,
                        }]
                      }}
                      options={{
                        ...chartOptions,
                        plugins: {
                          ...chartOptions.plugins,
                          tooltip: { enabled: false },
                          legend: { display: false },
                          datalabels: { display: false }
                        }
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={4} lg={2}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} style={{ color: "#33b5e5" }} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  {i18n.t("campaignReport.sentMessages")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4} lg={2}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} style={{ color: "#aa66cc" }} />
                <Typography variant="h4" component="div">
                  {uniqueNumbers}
                </Typography>
                <Typography color="textSecondary">
                  {i18n.t("campaignReport.uniqueRecipients")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4} lg={2}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} style={{ color: "#00C851" }} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  {i18n.t("campaignReport.deliveredMessages")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">{i18n.t("campaignReport.deliver")}</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {delivered} {i18n.t("campaignReport.table.of")} {totalMessagesSent}
              </Typography>
            </Grid>
          </Grid>
          <div style={{ height: 10, borderRadius: 5, margin: "8px 0", backgroundColor: theme.mode === 'light' ? '#e0e0e0' : '#424242', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(delivered / (totalMessagesSent || 1)) * 100}%`, background: 'linear-gradient(90deg, #00C851 0%, #00e676 100%)', transition: 'width 0.5s ease-in-out' }}></div>
          </div>
        </Box>

        {/* Informações adicionais da campanha */}
        <Grid container spacing={3}>
          {(whatsappChartData.labels.length > 0 || campaign.whatsappId) && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar} style={{ background: 'linear-gradient(45deg, #00C851 30%, #00e676 90%)' }}>
                      <WhatsAppIcon />
                    </Avatar>
                  }
                  title={
                    whatsappChartData.labels.length > 1 
                      ? i18n.t("campaignReport.whatsappConnections") 
                      : i18n.t("campaignReport.whatsappConnection")
                  }
                  subheader={
                    whatsappChartData.labels.length > 0
                      ? whatsappChartData.labels.join(" / ")
                      : (campaign.whatsapp?.name || i18n.t("campaignReport.notSpecified"))
                  }
                />
              </Card>
            </Grid>
          )}

          {campaign.tagListId && campaign.tagListId !== "Ninguna" && campaign.tagListId !== "Nenhuma" && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar} style={{ background: 'linear-gradient(45deg, #FF8800 30%, #ffbb33 90%)' }}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title={i18n.t("campaignReport.tagUsed")}
                  subheader={`ID: ${campaign.tagListId}`}
                />
              </Card>
            </Grid>
          )}

          {campaign.contactListId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar} style={{ background: 'linear-gradient(45deg, #FF8800 30%, #ffbb33 90%)' }}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title={i18n.t("campaignReport.contactLists")}
                  subheader={campaign.contactList?.name || i18n.t("campaignReport.notSpecified")}
                />
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar} style={{ background: "linear-gradient(45deg, #33b5e5 30%, #0099CC 90%)" }}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title={i18n.t("campaignReport.schedule")}
                subheader={datetimeToClient(campaign.scheduledAt) || i18n.t("campaignReport.notScheduled")}
              />
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar} style={{ background: "linear-gradient(45deg, #aa66cc 30%, #9933CC 90%)" }}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title={i18n.t("campaignReport.conclusion")}
                subheader={datetimeToClient(campaign.completedAt) || i18n.t("campaignReport.notConcluded")}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Seção de detalhes de mensagens */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              {i18n.t("campaignReport.table.details")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              style={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              {i18n.t("campaignReport.buttons.export")}
            </Button>
          </Box>
          <Divider />

          <TableContainer className={classes.tableContainer}>
            <Table aria-label="tabela de mensagens">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("campaignReport.table.id")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.recipient")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.message")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.sentAt")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.deliveredAt")}</TableCell>
                  <TableCell>{i18n.t("campaignReport.table.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messageRows.map((row, index) => (
                  <TableRow key={row.id || row.number || index}>
                    <TableCell>{row.jobId}</TableCell>
                    <TableCell>
                      <Tooltip title={row.number}>
                        <span>{formatPhoneNumber(row.number)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.fullMessage}>
                        <span>{row.message}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.deliveredAt || '-'}</TableCell>
                    <TableCell>{getMessageStatusChip(row.status)}</TableCell>
                  </TableRow>
                ))}
                {messageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {loading ? i18n.t("campaignReport.table.loading") : i18n.t("campaignReport.table.noData")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalMessagesSent}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                setPage(newPage);
                // Calcular qual página de dados carregar do servidor
                const serverPage = newPage + 1;
                loadMoreShipping(serverPage, rowsPerPage);
              }}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={i18n.t("campaignReport.table.linesPerPage")}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${i18n.t("campaignReport.table.of")} ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default CampaignReport;
